import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.milstudio.in';

const TableList = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [loading, setLoading] = useState(true);
  const [showChairPopup, setShowChairPopup] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedChairs, setSelectedChairs] = useState([]);
  const [chairs, setChairs] = useState([]);
  const [chairsLoading, setChairsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, tableId: null, tableName: '' });

  // Move fetchTables outside useEffect
  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}list-table.php`);
      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        setTables(data.data);
      } else {
        showToast('Failed to fetch tables', 'error');
      }
    } catch (error) {
      showToast('Error fetching tables', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (showChairPopup) {
      setChairsLoading(true);
      fetch(`${API_URL}get-available-chairs.php`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status && Array.isArray(data.data)) {
            setChairs(data.data);
          } else {
            setChairs([]);
          }
        })
        .catch(() => setChairs([]))
        .finally(() => setChairsLoading(false));
    } else {
      setChairs([]);
    }
  }, [showChairPopup]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const filteredTables = tables.filter(table => {
    const matchesSearch = table.tablename.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (table) => {
    navigate(`/add-table?id=${table.id}`, { state: { table } });
    showToast(`Editing table: ${table.tablename}`, 'info');
  };

  const handleDeleteClick = (table) => {
    setDeleteModal({ show: true, tableId: table.id, tableName: table.tablename });
  };

  const handleDeleteConfirm = async () => {
    const { tableId, tableName } = deleteModal;
    try {
      const response = await fetch(`${API_URL}delete-table.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: tableId
        }),
      });

      const data = await response.json();
      
      if (data.status === true || data.message === 'Table deleted successfully') {
        setTables(tables.filter(table => table.id !== tableId));
        showToast(`Table "${tableName}" deleted successfully`, 'success');
      } else {
        showToast(data.message || 'Failed to delete table', 'error');
      }
    } catch (error) {
      console.error('Error deleting table:', error);
      showToast('Failed to delete table. Please try again.', 'error');
    } finally {
      setDeleteModal({ show: false, tableId: null, tableName: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, tableId: null, tableName: '' });
  };

  const handleAddTable = () => {
    navigate('/add-table');
    showToast('Navigating to add new table', 'info');
  };

  const handlePlus = (table) => {
    setSelectedTable(table);
    setShowChairPopup(true);
    setSelectedChairs([]); // Reset selection when opening
  };

  const handleChairCardClick = (chairId) => {
    setSelectedChairs((prev) =>
      prev.includes(chairId)
        ? prev.filter((id) => id !== chairId)
        : [...prev, chairId]
    );
  };

  const closeChairPopup = () => {
    setShowChairPopup(false);
    setSelectedTable(null);
    setSelectedChairs([]);
  };

  const handleAssignChairs = async () => {
    if (!selectedTable || selectedChairs.length === 0) {
      showToast('Please select at least one chair.', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}assign-chairs.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tablename_id: selectedTable.id,
          chair_ids: selectedChairs,
        }),
      });
      const data = await response.json();
      if (data.status) {
        showToast('Chairs assigned successfully!', 'success');
        closeChairPopup();
        fetchTables(); // Refresh table list after successful assignment
      } else {
        showToast(data.message || 'Failed to assign chairs', 'error');
      }
    } catch (error) {
      showToast('Error assigning chairs', 'error');
    }
  };

  return (
    <div className="table-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Table Management</h1>
          <p>Manage restaurant tables</p>
        </div>
        <button className="add-unit-button" onClick={handleAddTable}>
          + Add New Table
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search tables by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        {loading ? (
          <div className="loading">Loading tables...</div>
        ) : (
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Table Name</th>
                <th>Room Type</th>
                <th>Assigned Chairs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTables.map(table => (
                <tr key={table.id}>
                  <td>
                    <div className="unit-info">
                      <div className="unit-icon">🍽️</div>
                      <span>{table.tablename}</span>
                    </div>
                  </td>
                  <td>
                    {table.room_type_name ? table.room_type_name : <span style={{ color: '#888' }}>N/A</span>}
                  </td>
                  <td>
                    {table.assigned_chairs && table.assigned_chairs.length > 0
                      ? table.assigned_chairs.map(chair => chair.chairname).join(', ')
                      : <span style={{ color: '#888' }}>None</span>
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn plus"
                        title="Assign Chairs"
                        onClick={() => handlePlus(table)}
                        style={{ marginLeft: '8px', fontWeight: 'bold', fontSize: '18px' }}
                      >
                        +
                      </button>
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(table)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteClick(table)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredTables.length} of {tables.length} tables
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showChairPopup && (
        <div className="popup-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(30, 41, 59, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="popup-content" style={{ background: '#fff', padding: 32, borderRadius: 16, minWidth: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#222', letterSpacing: 0.5 }}>Select Chairs for Table: ({selectedTable?.tablename})</h2>
            <div style={{ height: 1, background: '#eee', margin: '18px 0 20px 0' }} />
            <div style={{
              maxHeight: 260,
              overflowY: 'auto',
              margin: '0 0 24px 0',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 20,
              padding: 4,
            }}>
              {chairsLoading ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#888' }}>Loading chairs...</div>
              ) : chairs.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#888' }}>No chairs found.</div>
              ) : (
                chairs.map((chair) => {
                  const isSelected = selectedChairs.includes(chair.id);
                  return (
                    <div
                      key={chair.id}
                      onClick={() => handleChairCardClick(chair.id)}
                      style={{
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #2563eb' : '1.5px solid #e5e7eb',
                        borderRadius: 12,
                        padding: '18px 8px 12px 8px',
                        minWidth: 0,
                        minHeight: 90,
                        background: isSelected ? 'linear-gradient(135deg, #e0e7ff 0%, #f0f6ff 100%)' : '#f9fafb',
                        boxShadow: isSelected
                          ? '0 4px 16px rgba(37,99,235,0.10)'
                          : '0 2px 8px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.18s',
                        fontWeight: 500,
                        position: 'relative',
                        outline: isSelected ? '2px solid #2563eb' : 'none',
                      }}
                      onMouseOver={e => e.currentTarget.style.boxShadow = '0 6px 18px rgba(37,99,235,0.13)'}
                      onMouseOut={e => e.currentTarget.style.boxShadow = isSelected ? '0 4px 16px rgba(37,99,235,0.10)' : '0 2px 8px rgba(0,0,0,0.04)'}
                    >
                      <span style={{ fontSize: 34, marginBottom: 10, color: isSelected ? '#2563eb' : '#64748b', transition: 'color 0.18s' }}>🪑</span>
                      <span style={{ textAlign: 'center', fontSize: 15, color: '#222', fontWeight: 600, letterSpacing: 0.2 }}>{chair.chairname || chair.chairName}</span>
                      {isSelected && (
                        <span style={{
                          position: 'absolute',
                          top: 8,
                          right: 10,
                          color: '#2563eb',
                          fontSize: 18,
                          fontWeight: 700,
                          background: '#fff',
                          borderRadius: '50%',
                          width: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(37,99,235,0.10)'
                        }}>✓</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={closeChairPopup} style={{ padding: '7px 22px', borderRadius: 6, border: 'none', background: '#f3f4f6', color: '#222', fontWeight: 500, fontSize: 15, cursor: 'pointer', transition: 'background 0.18s' }}>Close</button>
              <button onClick={handleAssignChairs} style={{ padding: '7px 22px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', boxShadow: '0 2px 8px rgba(37,99,235,0.10)', transition: 'background 0.18s' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the table "<strong>{deleteModal.tableName}</strong>"?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleDeleteCancel}>
                No
              </button>
              <button className="btn btn-danger" onClick={handleDeleteConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableList; 